import React, { ReactNode, MouseEvent, useEffect } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';

interface ModalProps {
    /** モーダルの表示／非表示 */
    isOpen: boolean;
    /** 閉じる処理（×ボタン・オーバーレイクリック・ESC 等） */
    onClose: () => void;
    /** モーダル内部に表示するコンテンツ */
    children: ReactNode;
    /** オーバーレイ部分のカスタムクラス */
    overlayClassName?: string;
    /** モーダル本体のカスタムクラス */
    contentClassName?: string;
    /** ×ボタンのカスタムクラス */
    closeButtonClassName?: string;
    /** 右上の × ボタンを非表示にするか */
    hideCloseButton?: boolean;
    /** ESC キーで閉じる動作を無効化するか */
    disableCloseOnEsc?: boolean;
    /** オーバーレイをクリックした際に閉じる動作を無効化するか */
    disableCloseOnOverlay?: boolean;
}

/**
 * 汎用モーダルコンポーネント
 *
 * 基本機能:
 *  - 背景オーバーレイで背面を操作不可にする
 *  - 右上に × ボタン
 *  - ESC キー、オーバーレイクリックで閉じる（無効化オプションあり）
 *  - tailwind / 任意クラスの上書きでデザイン変更可
 */
const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    children,
    overlayClassName = '',
    contentClassName = '',
    closeButtonClassName = '',
    disableCloseOnEsc = false,
    disableCloseOnOverlay = false,
    hideCloseButton = false,
}) => {
    // ESC キー対応 & body スクロール禁止
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !disableCloseOnEsc) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        // body スクロール禁止
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // クリーンアップ
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = originalOverflow;
        };
    }, [isOpen, onClose, disableCloseOnEsc]);

    if (!isOpen) return null;

    const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
        if (disableCloseOnOverlay) return;
        onClose();
    };

    const handleContentClick = (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    const modalElement = (
        <div
            className={clsx(
                'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50',
                overlayClassName,
            )}
            onClick={handleOverlayClick}
        >
            <div
                className={clsx(
                    'relative bg-white rounded-2xl shadow-lg max-h-[90vh] overflow-auto mx-4',
                    contentClassName,
                )}
                onClick={handleContentClick}
            >
                {/* 閉じるボタン（必要な場合のみ表示） */}
                {!hideCloseButton && (
                    <button
                        type="button"
                        aria-label="Close modal"
                        className={clsx(
                            'absolute top-2 right-2 text-lg leading-none text-gray-400 hover:text-gray-700',
                            closeButtonClassName,
                        )}
                        onClick={onClose}
                    >
                        ×
                    </button>
                )}
                {children}
            </div>
        </div>
    );

    // Portal で body 直下に描画
    return ReactDOM.createPortal(modalElement, document.body);
};

export default Modal;
